import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { knowledgeAPI, categoryAPI } from "../services/api";
import KnowledgeCard from "../components/knowledge/KnowledgeCard";
import Pagination from "../components/common/Pagination";

const KnowledgePage = () => {
  const [knowledge, setKnowledge] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.getAll();
        setCategories(response.data.data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch knowledge
  useEffect(() => {
    const fetchKnowledge = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          search: debouncedSearch,
          category: selectedCategory,
        };

        console.log("🔍 Fetching with params:", params);

        const response = await knowledgeAPI.getAll(params);

        console.log("📦 API Response:", response.data);
        console.log("📊 Found:", response.data.data?.length, "items");

        // ตรวจสอบกรณีที่ควรจะไม่เจอข้อมูล
        if (debouncedSearch === "ไข่เค็ม" && selectedCategory === "2") {
          console.log("⚠️ Should be 0 results for 'ไข่เค็ม' in category 2");
          if (response.data.data?.length > 0) {
            console.error("❌ Bug: Found results when should be 0");
          }
        }

        setKnowledge(response.data.data);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("❌ Failed to fetch knowledge:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledge();
  }, [pagination.page, debouncedSearch, selectedCategory]);

  useEffect(() => {
    console.log("Current filters:", {
      searchTerm,
      debouncedSearch,
      selectedCategory,
    });
  }, [searchTerm, debouncedSearch, selectedCategory]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // ใน KnowledgePage.js
  const handleCategoryChange = (catId) => {
    console.log("Category changed to:", catId, "type:", typeof catId);
    setSelectedCategory(catId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <Helmet>
        <title>คลังปัญญา | งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ</title>
        <meta
          name="description"
          content="คลังปัญญาและองค์ความรู้ งานบริการวิชาการ มหาวิทยาลัยราชภัฏศรีสะเกษ"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 py-12">
        <div className="container-custom">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
              <i className="fas fa-brain text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4 relative inline-block pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-secondary-500 after:rounded-full">
              คลังปัญญา
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              รวมองค์ความรู้ โครงการบริการวิชาการ เพื่อการเรียนรู้ตลอดชีวิต
            </p>
          </div>

          {/* Filters Section */}
          <div className="mb-8 space-y-4">
            {/* Search Box */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="ค้นหาองค์ความรู้..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => handleCategoryChange("")}
                className={`px-4 py-2 rounded-full transition-all ${
                  selectedCategory === ""
                    ? "bg-primary-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                ทั้งหมด
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.catkm_id}
                  onClick={() => handleCategoryChange(cat.catkm_id.toString())} // แปลงเป็น string
                  className={`px-4 py-2 rounded-full transition-all flex items-center gap-1 ${
                    selectedCategory === cat.catkm_id.toString() // เปรียบเทียบเป็น string
                      ? "bg-primary-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  <i className={cat.catkm_icon}></i>
                  <span>{cat.catkm_name}</span>
                </button>
              ))}
            </div>

            {/* Active Filters */}
            {(searchTerm || selectedCategory) && (
              <div className="flex flex-wrap items-center justify-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <span className="text-gray-600">ตัวกรองที่ใช้งาน:</span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full">
                    <i className="fas fa-search text-xs"></i>
                    {searchTerm}
                  </span>
                )}
                {selectedCategory && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full">
                    <i className="fas fa-folder"></i>
                    {categories.find(
                      (c) =>
                        c.catkm_id.toString() === selectedCategory.toString(),
                    )?.catkm_name || "ไม่พบหมวดหมู่"}
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-red-500 hover:text-red-700 transition-colors text-sm"
                >
                  <i className="fas fa-times-circle mr-1"></i>
                  ล้างตัวกรอง
                </button>
              </div>
            )}
          </div>

          {/* Results Info */}
          {!loading && knowledge.length > 0 && (
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                พบทั้งหมด{" "}
                <span className="font-semibold text-primary-500">
                  {pagination.total}
                </span>{" "}
                รายการ
                {(searchTerm || selectedCategory) && (
                  <span className="ml-2 text-sm text-gray-500">
                    (จากตัวกรองที่เลือก)
                  </span>
                )}
              </p>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  setPagination((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value),
                    page: 1,
                  }));
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="12">12 รายการ/หน้า</option>
                <option value="24">24 รายการ/หน้า</option>
                <option value="36">36 รายการ/หน้า</option>
              </select>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="spinner"></div>
            </div>
          ) : knowledge.length > 0 ? (
            <>
              {/* Knowledge Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {knowledge.map((item) => (
                  <KnowledgeCard key={item.knowledge_id} knowledge={item} />
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            // No Results
            <div className="bg-white max-w-md mx-auto text-center py-16">
              <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-search text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                ไม่พบข้อมูล
              </h3>
              <p className="text-gray-600 mb-8">
                ไม่พบองค์ความรู้ที่ตรงกับคำค้นหา กรุณาลองใหม่อีกครั้ง
              </p>
              <button
                onClick={clearFilters}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <i className="fas fa-redo-alt mr-2"></i>
                ล้างตัวกรองทั้งหมด
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default KnowledgePage;
